import { observer } from "mobx-react";
import React from "react";

import BaseList from "@component/BaseList";
import Button, { ButtonVariant } from "@eman/emankit/Button";
import Certification from "@model/Certification";
import CertificationListVM from "@vm/List/Certification";
import { UserRightsObjects, UserRightsOperations } from "@model/Rights";
import { AlignType } from "@eman/respectkit/DataTable/DataBody";

import { lazyInject, TYPES } from "../../../inversify.config";
import ViewHelpers from "@util/ViewHelpers";

@observer
export default class CertificationList extends BaseList<Certification, CertificationListVM, PropsFromRouter> {
  modelName = "certification";
  prefix = "employee";
  searchable = false;
  savedSettingsName = "user_certification";

  @lazyInject(TYPES.CertificationList)
  vm: CertificationListVM;

  initVM() {
    this.vm.init(this.props.match.params.user_id);
  }

  renderRow = (item: Certification, column: string): React.ReactNode => {
    switch (column) {
      case "enumeration_certificate_issuer_id":
        return item.certificate_issuer ? item.certificate_issuer.name : "";
      case "created_at":
      case "date_from":
      case "date_to":
        return this.locs.formatDate(item.created_at);
      case "credential_url":
        return ViewHelpers.renderUrlButton(this.locs, item.credential_url);
      default:
        return item[column];
    }
  };

  onAddClick = () => {
    this.router.pageLink(this.uriHelper.new_employees_certifications(this.props.match.params.user_id));
  };

  headerProps() {
    return {
      text: this.tg("title"),
      level: 2,
      buttons: () => {
        if (this.user.allowToObject(UserRightsObjects.CERTIFICATION, UserRightsOperations.CREATE)) {
          return (
            <Button variant={ButtonVariant.Primary} key="new_certification" onClick={this.onAddClick}>
              {this.tg("add")}
            </Button>
          );
        }

        return null;
      },
    };
  }

  filterProps() {
    const filters: FilterData[] = [
      {
        id: "name",
        label: this.ta("name"),
        type: "search",
      },
      {
        id: "date_from",
        label: this.ta("date_from"),
        type: "date_range",
      },
      {
        id: "date_to",
        label: this.ta("date_to"),
        type: "date_range",
      },
    ];

    return {
      filters,
      hideReset: false,
      hideFilterSettings: false,
      hideColumnSettings: false,
    };
  }

  dataProps() {
    const noClickAttributes = {
      width: 100,
      align: AlignType.Center,
      className: "noclick",
      alwaysShow: true,
      nosort: true,
    };

    const columns = [
      this.createField("name", {}),
      this.createField("enumeration_certificate_issuer_id", { nosort: true }),
      this.createField("date_from", {}),
      this.createField("date_to", { width: 100 }),
      this.createField("credential_id", {}),
      this.createField("credential_url", { ...noClickAttributes, alwaysShow: false }),
    ];

    return {
      columns,
      value: this.renderRow,
      fixedHeader: false,
      onClick: (item: Certification) => {
        this.objectEditOrShowLink("employees_certifications", [this.props.match.params.user_id, item.id]);
      },
    };
  }
}
